<script lang="ts">
import { reactive, defineComponent, computed, ref, PropType } from '@vue/composition-api';
import { UserNotificationModel } from '@/store/global/notifications/types';
import { NotificationSourceType } from '@/api/notifications/queries';

import * as notificationsModule from '@/store/global/notifications';
import { useForm } from '@/_core/utils/forms';
import { required } from '@/_core/utils/forms/validators';
import { wait } from '@/_core/utils/misc';
import { ReplyNotification } from '@/store/global/notifications';

interface IProps {
    item: UserNotificationModel;
}

const NotificationListItem = defineComponent({
    name: 'NotificationListItem',
    props: {
        item: Object as PropType<UserNotificationModel>,
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setup(props: IProps, ctx) {
        const state = reactive({
            replyText: '',
            mounted: false,
        });

        const store = notificationsModule.useNotificationsStore();

        const header = computed(() => {
            let content = '';

            switch (props.item.notificationSourceType) {
                case NotificationSourceType.MentionedInRoadmapCardComment:
                    content += ` mentioned you on ${props.item.relatedCardName} roadmap card`;
                    break;

                case NotificationSourceType.MentionedInFeedbackComment:
                    content += ` mentioned you on an insight`;
                    break;

                case NotificationSourceType.MentionedInIdeaComment:
                    content += ` mentioned you on ${props.item.relatedCardName} idea (idea board)`;
                    break;

                case NotificationSourceType.MentionedInRoadmapIdeaComment:
                    content += ` mentioned you on ${props.item.relatedCardName} idea (roadmap)`;
                    break;

                case NotificationSourceType.MentionedInBoardComment:
                    content += ' mentioned you in board discussion';
                    break;

                case NotificationSourceType.MentionedInStorymapCardDescription:
                    content += ` mentioned you on ${props.item.relatedCardName} card`;
                    break;

                case NotificationSourceType.MentionedInCardComment:
                    content += ` mentioned you on ${props.item.relatedCardName} card`;
                    break;

                case NotificationSourceType.CardCommentAdded:
                    content += ` commented on ${props.item.relatedCardName}`;
                    break;

                case NotificationSourceType.BoardCommentAdded:
                    content += 'sent a new message on the discussion panel';
                    break;
            }

            return content;
        });

        const notificationText = header.value.length
            ? props.item.notificationText
            : `${props.item.sourceUserName} ${props.item.notificationText}`;

        const onMarkAsRead = () => {
            ctx.emit('onDismissNotification', props.item.id);
        };

        const isOpenCardVisible = computed(() => {
            return props.item.relatedCardId && props.item.storymapSlug;
        });

        const onOpenCard = () => {
            ctx.emit('onOpenCard', {
                storyMapSlug: props.item.storymapSlug,
                cardId: props.item.relatedCardId,
                wsSubDomain: props.item.workspaceSubdomain,
            });
        };

        const isOpenStoryMapVisible = computed(() => {
            return !props.item.relatedCardId && props.item.storymapSlug && !props.item.isAcceptableInvitation.value;
        });

        const onOpenStoryMap = () => {
            const link =
                props.item.notificationSourceType === NotificationSourceType.MentionedInBoardComment ||
                props.item.notificationSourceType === NotificationSourceType.BoardCommentAdded
                    ? `${props.item.storymapSlug}?replyToComment=${props.item.notificationSourceId}`
                    : props.item.storymapSlug;
            ctx.emit('onOpenStoryMap', link, props.item.workspaceSubdomain);
        };

        const isOpenIdeaVisible = computed(() => {
            return props.item.relatedCardId && props.item.notificationSourceType === NotificationSourceType.MentionedInIdeaComment;
        });

        const isOpenFeedbackVisible = computed(() => {
            return props.item.relatedCardId && props.item.notificationSourceType === NotificationSourceType.MentionedInFeedbackComment;
        });

        const isOpenRoadmapCardVisible = computed(() => {
            return props.item.relatedCardId && props.item.notificationSourceType === NotificationSourceType.MentionedInRoadmapCardComment;
        });

        const onOpenIdea = () => {
            ctx.emit('onOpenIdea', props.item.relatedCardId, props.item.productSlug);
        };

        const onOpenFeedback = () => {
            ctx.emit('onOpenFeedback', props.item.relatedCardId, props.item.productSlug);
        };

        const onOpenRoadmapCard = () => {
            ctx.emit('onOpenRoadmapCard', props.item.relatedCardId, props.item.roadmapId);
        };

        const isReplyButtonVisible = computed(() => {
            return props.item.isComment.value && store.state.storymapsWhereCommentingEnabled.includes(props.item.storymapId);
        });

        const onAcceptInvitation = () => {
            ctx.emit('onAcceptInvitation', props.item.notificationSourceId);
        };

        const isAcceptInvitationVisible = computed(() => {
            return props.item.isAcceptableInvitation.value;
        });

        const replyField = ref(null);

        const onReply = async () => {
            isReplyFieldVisible.value = true;
            // @ts-ignore
            replyField.value.focus();
        };

        const onEscReply = async () => {
            await wait(200);
            isReplyFieldVisible.value = false;
            state.replyText = '';
        };

        const formElement = ref('');
        const form = useForm(formElement, { required });

        const isReplyFieldVisible = ref<boolean>(false);

        const onSaveReply = () => {
            form.saveForm(async () => {
                await store.dispatch(new ReplyNotification(props.item, state.replyText));
                isReplyFieldVisible.value = false;
                state.replyText = '';
            });
        };

        return {
            state,
            header,
            notificationText,
            onMarkAsRead,

            NotificationSourceType,

            isOpenCardVisible,
            onOpenCard,

            isOpenIdeaVisible,
            onOpenIdea,

            isOpenRoadmapCardVisible,
            onOpenRoadmapCard,

            isOpenStoryMapVisible,
            onOpenStoryMap,

            onAcceptInvitation,
            isAcceptInvitationVisible,

            isReplyButtonVisible,
            onReply,

            onEscReply,
            isReplyFieldVisible,

            form,
            formElement,
            onSaveReply,
            replyField,

            isOpenFeedbackVisible,
            onOpenFeedback,
            // shouldRender,
            // onIntersect,
        };
    },
});

export default NotificationListItem;
</script>

<template>
  <v-hover v-slot="{ hover }">
    <v-list-item :key="item.id" :class="{ hover: hover }">
      <v-list-item-content>
        <v-progress-linear
          :active="form.isSaving"
          :indeterminate="true"
          absolute
          top
          color="primary lighten-2"
        />
        <v-list-item-title v-if="header.length" class="caption">
          <strong>{{ item.sourceUserName }}</strong>
        </v-list-item-title>
        <v-list-item-subtitle v-if="header.length">
          {{ header }}
        </v-list-item-subtitle>
        <AppExpandable collapsedHeight="100">
          <div
            v-if="!item.isMarkdownContent.value"
            ref="markdownContent"
            class="content"
            v-text="notificationText"
          />
          <div
            v-else
            ref="markdownContent"
            class="content"
            v-html="item.notificationMarkdownContent.value"
          />
        </AppExpandable>
        <div class="d-inline-flex caption surface ml-auto happened-at">
          on
          <span class="caption text-uppercase font-weigth-medium text-decoration-underline mx-1 ellipsis" style="max-width: 250px">
            {{ item.notificationSourceType === NotificationSourceType.MentionedInRoadmapCardComment ? item.roadmapName : item.storymapName }}
          </span>
          <timeago class="d-inline" :datetime="item.happenedAt" /><span v-if="item.isUpdated">(edited)</span>
        </div>
        <template v-if="item.replies.length > 0">
          <div v-for="reply in item.replies" :key="reply.CommentedAt">
            <div class="caption py-2">
              <v-icon x-small>
                $falReply
              </v-icon>
              <span class="ml-1">Your reply was "</span><span class="md-content" v-html="reply.commentMarkdownContent.value" />"
              <timeago class="d-inline" :datetime="reply.CommentedAt" />
            </div>
          </div>
        </template>
        <v-form
          v-show="isReplyButtonVisible && isReplyFieldVisible"
          ref="formElement"
          v-model="form.valid"
          @keydown.native.enter.prevent="onSaveReply"
          @keydown.native.esc.stop="onEscReply"
        >
          <v-text-field
            ref="replyField"
            v-model="state.replyText"
            autofocus
            :rules="[form.rules.required()]"
            :disabled="form.isSaving"
            label="reply"
            clearable
            @blur="onEscReply"
          />
        </v-form>
      </v-list-item-content>

      <v-list-item-action absolute>
        <v-menu
          v-if="isReplyButtonVisible || 
            isOpenCardVisible || 
            isOpenStoryMapVisible || 
            isAcceptInvitationVisible || 
            !item.isDismissed ||
            isOpenIdeaVisible || 
            isOpenFeedbackVisible ||
            isOpenRoadmapCardVisible"
          offset-y
        >
          <template #activator="{ on: menu }">
            <v-tooltip top>
              <template #activator="{ on: tooltip }">
                <v-btn
                  v-show="hover"
                  color="secondaryButton"
                  depressed
                  fab
                  small
                  absolute
                  class="action-button"
                  v-on="{ ...tooltip, ...menu }"
                >
                  <v-icon>
                    $falEllipsisV
                  </v-icon>
                </v-btn>
              </template>
              <span>Actions</span>
            </v-tooltip>
          </template>
          <v-card min-width="290" max-width="320">
            <v-list dense nav>
              <v-list-item v-if="isReplyButtonVisible" @click="onReply">
                <v-list-item-title>reply</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isOpenCardVisible" @click="onOpenCard">
                <v-list-item-title>view card details</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isOpenStoryMapVisible" @click="onOpenStoryMap">
                <v-list-item-title>open story map</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isOpenIdeaVisible" @click="onOpenIdea">
                <v-list-item-title>open idea</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isOpenFeedbackVisible" @click="onOpenFeedback">
                <v-list-item-title>open insight</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isOpenRoadmapCardVisible" @click="onOpenRoadmapCard">
                <v-list-item-title>open roadmap card</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isAcceptInvitationVisible" @click="onAcceptInvitation">
                <v-list-item-title>accept invitation</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="!item.isDismissed" @click="onMarkAsRead">
                <v-list-item-title>mark as read</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </v-list-item-action>
    </v-list-item>
  </v-hover>
</template>

<style lang="scss" scoped>
.action-button {
    top: 0;
    bottom: 0;
    right: 0;
    margin: auto;
}

.v-list-item {
    &:before {
        background-color: currentColor;
        bottom: 0;
        content: '';
        left: 0;
        opacity: 0;
        pointer-events: none;
        position: absolute;
        right: 0;
        top: 0;
        transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    }
    &.hover {
        &:before {
            opacity: 0.05;
        }
    }

    &__action {
        position: absolute;
        right: 16px;
        top: 0;
        bottom: 0;
    }
}

.md-content {
    ::v-deep p {
        display: inline;
    }
}

.content {
    font-size: 0.875rem;
    line-height: 1.5;
}

.happened-at {
    flex: 0 0 auto;
    padding: 0 5px;
    font-size: 10px !important;
    transform: translateY(5px);
}

::v-deep .mention {
    color: var(--v-primary-base);
}
</style>
